import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxItem,
	ComboboxList,
	ComboboxTrigger,
	ComboboxValue,
	DatePicker,
	FILTER_CONTROL_HEIGHT_CLASS,
	FilterBar,
	FilterField,
	Input,
	useComboboxAnchor,
} from "@components";
import {
	styles as buttonStyles,
	Button as UntitledButton,
} from "@components/button/button-untitledui";
import { CalendarDate } from "@internationalized/date";
import { Download01 } from "@untitledui/icons";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import type { DateValue } from "react-aria";
import { cx } from "@/utils/cx";
import { REPORT_SUMMARY_CATEGORY_NAMES } from "../constants";
import type { ExpenseTypeCode } from "../types";

type ExpenseCategorySelectItem = {
	id: ExpenseTypeCode;
	label: string;
};

const PLATE_INPUT_ID = "expenses-history-plate";
const PLATE_DEBOUNCE_MS = 1000;

function stringToDateValue(str: string | null): CalendarDate | null {
	if (!str) {
		return null;
	}
	const [y, m, d] = str.split("-").map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
		return null;
	}
	return new CalendarDate(y, m, d);
}

function normalizePlate(value: string): string {
	return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export type ExpensesHistoryFiltersState = {
	plate: string;
	types?: ExpenseTypeCode[];
	startDate: DateValue;
	endDate: DateValue;
};

type ExpensesHistoryFiltersProps = {
	filters: ExpensesHistoryFiltersState;
	onFiltersChange: (f: ExpensesHistoryFiltersState) => void;
	onPageChange: (page: number) => void;
	onExport: () => void;
};

export function ExpensesHistoryFilters({
	filters,
	onFiltersChange,
	onPageChange,
	onExport,
}: ExpensesHistoryFiltersProps) {
	const anchor = useComboboxAnchor();

	const expenseTypeItems = useMemo<ExpenseCategorySelectItem[]>(
		() =>
			(Object.entries(REPORT_SUMMARY_CATEGORY_NAMES) as [string, string][]).map(
				([key, label]) => ({
					id: Number(key) as ExpenseTypeCode,
					label,
				})
			),
		[]
	);

	const selectedExpenseTypes = useMemo(() => {
		if (!filters.types?.length) {
			return [];
		}

		return filters.types
			.map((id) => expenseTypeItems.find((item) => item.id === id))
			.filter((item): item is ExpenseCategorySelectItem => item !== undefined);
	}, [filters.types, expenseTypeItems]);

	const [search, setSearch] = useState(filters.plate ?? "");
	const latestRef = useRef({ filters, onFiltersChange, onPageChange });
	latestRef.current = { filters, onFiltersChange, onPageChange };

	const update = (patch: Partial<ExpensesHistoryFiltersState>) => {
		const current = latestRef.current;
		current.onFiltersChange({ ...current.filters, ...patch });
		current.onPageChange(1);
	};

	const applyPlate = useMemo(
		() =>
			debounce((value: string) => {
				const current = latestRef.current;
				const plate = normalizePlate(value);
				// Sem mudança efetiva da placa, a listagem continua na mesma página.
				if (plate === (current.filters.plate ?? "")) {
					return;
				}
				current.onFiltersChange({ ...current.filters, plate });
				current.onPageChange(1);
			}, PLATE_DEBOUNCE_MS),
		[]
	);

	// Uma busca pendente não deve ser aplicada depois que a barra sai da tela.
	useEffect(() => () => applyPlate.cancel(), [applyPlate]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		setSearch(normalizePlate(value ?? ""));
		applyPlate(value);
	};

	return (
		<FilterBar
			actions={
				<UntitledButton
					aria-label="Exportar despesas"
					color="secondary"
					iconLeading={Download01}
					onClick={onExport}
					size="sm"
					type="button"
				>
					Exportar
				</UntitledButton>
			}
			className="mb-0"
			label="Filtros do histórico de despesas"
		>
			<FilterField
				className="sm:w-auto sm:min-w-[220px] sm:max-w-[600px] sm:flex-1"
				htmlFor={PLATE_INPUT_ID}
				label="Placa"
			>
				<Input
					className={FILTER_CONTROL_HEIGHT_CLASS}
					id={PLATE_INPUT_ID}
					maxLength={7}
					onChange={handleSearchChange}
					placeholder="Ex: ABC1D23"
					value={search}
				/>
			</FilterField>

			<FilterField className="sm:max-w-[246px]" label="Tipo">
				<div className="w-full" ref={anchor}>
					<Combobox
						autoHighlight
						filter={(_itemValue, _query) => true}
						items={expenseTypeItems}
						itemToStringValue={(item: ExpenseCategorySelectItem) =>
							String(item.id)
						}
						multiple
						onValueChange={(fieldValue) => {
							let nextTypes: ExpenseTypeCode[] | undefined;
							if (fieldValue.length > 0) {
								nextTypes = fieldValue.map((item) => item.id);
							} else {
								nextTypes = undefined;
							}
							update({ types: nextTypes });
						}}
						value={selectedExpenseTypes}
					>
						<ComboboxTrigger
							render={
								<button
									aria-label="Filtrar por tipo"
									className={cx(
										buttonStyles.common.root,
										buttonStyles.sizes.sm.root,
										buttonStyles.colors.secondary.root,
										`${FILTER_CONTROL_HEIGHT_CLASS} w-full justify-start px-3.5 font-normal text-secondary text-sm`
									)}
									tabIndex={0}
									type="button"
								>
									<span className="w-full max-w-full truncate">
										<ComboboxValue>
											{(cbxState: ExpenseCategorySelectItem[]) => {
												const formattedValue = (
													values: ExpenseCategorySelectItem[]
												) => {
													if (values.length === 0) {
														return "Todos";
													}
													if (values.length === 1) {
														return values[0].label ?? "";
													}
													return `${values.length} tipos`;
												};
												return formattedValue(cbxState);
											}}
										</ComboboxValue>
									</span>
								</button>
							}
						/>
						<ComboboxContent anchor={anchor} className="w-[220px] bg-white">
							<ComboboxEmpty>Nenhum tipo encontrado.</ComboboxEmpty>
							<ComboboxList>
								{(item) => (
									<ComboboxItem
										className="h-9 min-h-fit text-sm"
										key={item.id}
										value={item}
									>
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
			</FilterField>

			<FilterField className="sm:max-w-[246px]" label="Data início">
				<DatePicker
					aria-label="Data início"
					className="w-full"
					maxValue={filters.endDate}
					onChange={(date) => {
						if (!date) {
							return;
						}
						update({ startDate: date });
					}}
					size="sm"
					value={stringToDateValue(
						filters.startDate ? filters.startDate.toString() : null
					)}
				/>
			</FilterField>

			<FilterField className="sm:max-w-[246px]" label="Data fim">
				<DatePicker
					aria-label="Data fim"
					className="w-full"
					minValue={filters.startDate}
					onChange={(date) => {
						if (!date) {
							return;
						}
						update({ endDate: date });
					}}
					size="sm"
					value={stringToDateValue(
						filters.endDate ? filters.endDate.toString() : null
					)}
				/>
			</FilterField>
		</FilterBar>
	);
}
